import type { TrendyolCredentials, TrendyolOrder, TrendyolOrdersResponse } from '../types';
import { trendyolApi } from '../../../../config/apiClient';

const TRENDYOL_STAGE_URL = 'https://stageapi.tgoapis.com';
const TRENDYOL_PROD_URL = 'https://api.tgoapis.com';

export type TrendyolPackageStatus =
  | 'Created'
  | 'Picking'
  | 'Invoiced'
  | 'Shipped'
  | 'Delivered'
  | 'Returned'
  | 'Cancelled'
  | 'Unsupplied';

export interface TrendyolCancelReason {
  id: number;
  label: string;
  model1Only: boolean;
}

export const TRENDYOL_CANCEL_REASONS: TrendyolCancelReason[] = [
  { id: 621, label: 'Tedarik problemi', model1Only: false },
  { id: 622, label: 'Mağaza kapalı', model1Only: false },
  { id: 623, label: 'Mağaza siparişi hazırlayamıyor', model1Only: false },
  { id: 624, label: 'Yüksek yoğunluk / Kurye yok', model1Only: true },
  { id: 626, label: 'Alan dışı', model1Only: true },
  { id: 627, label: 'Sipariş karışıklığı', model1Only: false },
];

/**
 * Trendyol Yemek API'sinin attığı 409 Conflict hatasını temsil eder.
 * Trendyol durum makinesi deterministiktir: state'e uygun olmayan transition denersek
 * (örn. Created sipariş için /packages/invoiced çağrısı) `ClientApiDeterministicConflictException`
 * fırlatır. Bu sınıf, hook tarafındaki self-heal akışını tetiklemek için kullanılır.
 *
 * useTrendyolYemek.updateOrderStatus içinde `instanceof TrendyolConflictError` ile
 * yakalanır ve siparişin gerçek durumu Trendyol'dan tekrar çekilir.
 */
export class TrendyolConflictError extends Error {
  packageId: string;
  rawBody: string;
  status: number;
  constructor(packageId: string, rawBody: string, status = 409) {
    super(`Trendyol durum çakışması (HTTP ${status})`);
    this.name = 'TrendyolConflictError';
    this.packageId = packageId;
    this.rawBody = rawBody;
    this.status = status;
  }
}

/**
 * Trendyol'un 409 Conflict gövdesinden siparişin **gerçek** mevcut durumunu çıkarır.
 *
 * Tipik mesaj formatı (Türkçe):
 *   "ClientApiDeterministicConflictException ... paketi için **Delivered** statüsünden **Invoiced** statüsüne geçilemez ..."
 *
 * "X statüsünden Y statüsüne" şeklinde — X mevcut, Y hedef. X bizim için daha kıymetli.
 * İngilizce fallback de denenir: "from X to Y".
 *
 * @returns Eşleşen geçerli `TrendyolPackageStatus` ya da parse edilemezse `null`.
 */
export function parseStatusFromConflictBody(rawBody: string | undefined | null): TrendyolPackageStatus | null {
  if (!rawBody) return null;

  const validStatuses: TrendyolPackageStatus[] = [
    'Created', 'Picking', 'Invoiced', 'Shipped', 'Delivered', 'Returned', 'Cancelled', 'Unsupplied',
  ];
  const matchValid = (candidate: string): TrendyolPackageStatus | null => {
    const lower = candidate.toLowerCase();
    return validStatuses.find(s => s.toLowerCase() === lower) ?? null;
  };

  // Türkçe: "X statüsünden Y statüsüne" (statüsünde / statüsünü gibi varyantları da yakala)
  const trMatch = rawBody.match(/([A-Za-zÇĞİıÖŞÜçğşöü]+)\s+stat[uü]s[uü]n/i);
  if (trMatch) {
    const matched = matchValid(trMatch[1]);
    if (matched) return matched;
  }

  // İngilizce fallback: "from X to Y"
  const enMatch = rawBody.match(/from\s+([A-Za-z]+)\s+to/i);
  if (enMatch) {
    const matched = matchValid(enMatch[1]);
    if (matched) return matched;
  }

  // Son çare: gövde içinde geçen ilk geçerli statu kelimesini bul
  // (mesajın formatı değişirse bile en azından bir statu yakalayabiliriz)
  for (const status of validStatuses) {
    const re = new RegExp(`\\b${status}\\b`);
    if (re.test(rawBody)) {
      return status;
    }
  }

  return null;
}

function getBaseUrl(): string {
  const env = process.env.REACT_APP_TRENDYOL_ENVIRONMENT || 'STAGE';
  return env.toUpperCase() === 'PRODUCTION' ? TRENDYOL_PROD_URL : TRENDYOL_STAGE_URL;
}

function getAuthHeaders(credentials: TrendyolCredentials): Record<string, string> {
  const token = btoa(`${credentials.apiKey}:${credentials.apiSecret}`);
  const agentName = process.env.REACT_APP_TRENDYOL_AGENT_NAME || 'Aquasoft';

  return {
    'Authorization': `Basic ${token}`,
    'Content-Type': 'application/json',
    'x-agentname': agentName,
    'x-executor-user': credentials.userEmail,
    'User-Agent': `${credentials.supplierId} - SelfIntegration`,
  };
}

async function proxyFetch(
  localServiceUrl: string,
  trendyolUrl: string,
  method: string,
  headers: Record<string, string>,
  body?: unknown,
): Promise<Response> {
  const proxyUrl = `${localServiceUrl}/api/trendyol-proxy`;

  const proxyBody: Record<string, unknown> = {
    method,
    url: trendyolUrl,
    headers,
  };

  if (body && method !== 'GET') {
    proxyBody.body = body;
  }

  const response = await fetch(proxyUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(proxyBody),
  });

  return response;
}

export async function fetchOrders(
  credentials: TrendyolCredentials,
  // Varsayılan: Delivered (teslim edildi) HARİÇ tüm durumlar.
  // Teslim edilen sipariş zaten SQL'de kayıtlı; Trendyol'dan tekrar çekmek
  // polling payload'ını gereksiz büyütür. Cancelled/Unsupplied dahil edilir
  // ki iptal durumu canlı görünebilsin.
  statuses: TrendyolPackageStatus[] = ['Created', 'Picking', 'Invoiced', 'Shipped', 'Unsupplied', 'Cancelled'],
  localServiceUrl: string = 'http://localhost:3003',
): Promise<TrendyolOrder[]> {
  // storeId ZORUNLU: Trendyol API-side filtreleme için query'e eklenir.
  // Eksikse silent bug olmasın diye erken throw ediyoruz; hook zaten
  // storeId olmadan polling'i başlatmıyor.
  if (!credentials.storeId) {
    throw new Error('[TrendyolYemek] storeId zorunlu (credentials.storeId boş)');
  }

  const baseUrl = getBaseUrl();
  const statusStr = statuses.join(',');
  const trendyolUrl =
    `${baseUrl}/integrator/order/meal/suppliers/${credentials.supplierId}/packages` +
    `?packageStatuses=${statusStr}` +
    `&storeId=${encodeURIComponent(String(credentials.storeId))}`;

  const response = await proxyFetch(localServiceUrl, trendyolUrl, 'GET', getAuthHeaders(credentials));

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Trendyol sipariş çekme hatası (${response.status}): ${errorText.substring(0, 200)}`);
  }

  const rawText = await response.text();
  console.log(`[TrendyolYemek] 📋 API ham yanıt (store=${credentials.storeId}, byte=${rawText.length}):`, rawText);

  const data: TrendyolOrdersResponse = JSON.parse(rawText);
  return data.content || [];
}

export async function acceptOrder(
  credentials: TrendyolCredentials,
  packageId: string,
  preparationTime: number = 30,
  localServiceUrl: string = 'http://localhost:3003',
): Promise<void> {
  const baseUrl = getBaseUrl();
  const trendyolUrl = `${baseUrl}/integrator/order/meal/suppliers/${credentials.supplierId}/packages/picked`;

  const response = await proxyFetch(
    localServiceUrl, trendyolUrl, 'PUT', getAuthHeaders(credentials),
    { packageId, preparationTime },
  );

  if (response.status === 409) {
    const errorText = await response.text().catch(() => '');
    throw new TrendyolConflictError(packageId, errorText, 409);
  }
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Sipariş kabul hatası (${response.status}): ${errorText.substring(0, 200)}`);
  }
}

export async function markReady(
  credentials: TrendyolCredentials,
  packageId: string,
  localServiceUrl: string = 'http://localhost:3003',
): Promise<void> {
  const baseUrl = getBaseUrl();
  const trendyolUrl = `${baseUrl}/integrator/order/meal/suppliers/${credentials.supplierId}/packages/invoiced`;

  const bodyData: Record<string, unknown> = { packageId };

  const response = await proxyFetch(
    localServiceUrl, trendyolUrl, 'PUT', getAuthHeaders(credentials),
    bodyData,
  );

  if (response.status === 409) {
    const errorText = await response.text().catch(() => '');
    throw new TrendyolConflictError(packageId, errorText, 409);
  }
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Sipariş hazır hatası (${response.status}): ${errorText.substring(0, 200)}`);
  }
}

export async function markShipped(
  credentials: TrendyolCredentials,
  packageId: string,
  localServiceUrl: string = 'http://localhost:3003',
): Promise<void> {
  const baseUrl = getBaseUrl();
  const trendyolUrl = `${baseUrl}/integrator/order/meal/suppliers/${credentials.supplierId}/packages/${packageId}/manual-shipped`;

  const response = await proxyFetch(
    localServiceUrl, trendyolUrl, 'PUT', getAuthHeaders(credentials),
    {},
  );

  if (response.status === 409) {
    const errorText = await response.text().catch(() => '');
    throw new TrendyolConflictError(packageId, errorText, 409);
  }
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Sipariş yola çıktı hatası (${response.status}): ${errorText.substring(0, 200)}`);
  }
}

export async function markDelivered(
  credentials: TrendyolCredentials,
  packageId: string,
  localServiceUrl: string = 'http://localhost:3003',
): Promise<void> {
  const baseUrl = getBaseUrl();
  const trendyolUrl = `${baseUrl}/integrator/order/meal/suppliers/${credentials.supplierId}/packages/${packageId}/manual-delivered`;

  const response = await proxyFetch(
    localServiceUrl, trendyolUrl, 'PUT', getAuthHeaders(credentials),
    {},
  );

  if (response.status === 409) {
    const errorText = await response.text().catch(() => '');
    throw new TrendyolConflictError(packageId, errorText, 409);
  }
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Sipariş teslim hatası (${response.status}): ${errorText.substring(0, 200)}`);
  }
}

export async function cancelOrder(
  credentials: TrendyolCredentials,
  packageId: string,
  itemIdList: string[],
  reasonId: number,
  localServiceUrl: string = 'http://localhost:3003',
): Promise<void> {
  const baseUrl = getBaseUrl();
  const trendyolUrl = `${baseUrl}/integrator/order/meal/suppliers/${credentials.supplierId}/packages/unsupplied`;

  const response = await proxyFetch(
    localServiceUrl, trendyolUrl, 'PUT', getAuthHeaders(credentials),
    { packageId, itemIdList, reasonId },
  );

  if (response.status === 409) {
    const errorText = await response.text().catch(() => '');
    throw new TrendyolConflictError(packageId, errorText, 409);
  }
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Sipariş iptal hatası (${response.status}): ${errorText.substring(0, 200)}`);
  }
}

export async function updateStoreStatus(
  credentials: TrendyolCredentials,
  status: 'OPEN' | 'CLOSED',
  localServiceUrl: string = 'http://localhost:3003',
): Promise<void> {
  const baseUrl = getBaseUrl();
  const trendyolUrl = `${baseUrl}/integrator/store/meal/suppliers/${credentials.supplierId}/stores/${credentials.storeId}/status`;

  const response = await proxyFetch(
    localServiceUrl, trendyolUrl, 'PUT', getAuthHeaders(credentials),
    { status },
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Restoran durum güncelleme hatası (${response.status}): ${errorText.substring(0, 200)}`);
  }
}

// ============================================================================
// SQL Persistence Functions (.NET API - gts_trendyolYmek_api)
// local-service proxy KULLANILMAZ, dogrudan .NET API'ye istek atilir
// ============================================================================

export interface SaveOrderResult {
  isPaid: boolean;
  invoiceId?: string;
  invoiceNumber?: string;
  paidAmount?: number;
  sqlStatus?: string;
}

export async function saveOrderToSql(order: TrendyolOrder, branchId?: string | null): Promise<SaveOrderResult | null> {
  try {
    const response = await trendyolApi.post('/api/trendyol/order/save-from-polling', {
      BranchId: branchId || undefined,
      OrderDataJson: JSON.stringify(order),
    });
    const result = response.data;
    const isPaid = result.isPaid ?? result.IsPaid ?? false;
    return {
      isPaid,
      invoiceId: result.invoiceId ?? result.InvoiceId ?? undefined,
      invoiceNumber: result.invoiceNumber ?? result.InvoiceNumber ?? undefined,
      paidAmount: result.paidAmount ?? result.PaidAmount ?? undefined,
      sqlStatus: result.sqlStatus ?? result.SqlStatus ?? undefined,
    };
  } catch (err) {
    console.error('[TrendyolSQL] save-from-polling exception:', err);
    return null;
  }
}

export async function updateStatusInSql(
  packageId: string,
  newStatus: string,
  courierInfo?: { courierId: number; courierName: string; courierUid: string },
  orderData?: TrendyolOrder,
  branchId?: string | null,
): Promise<void> {
  try {
    const body: Record<string, unknown> = {
      BranchId: branchId || undefined,
      PackageId: packageId,
      NewStatus: newStatus,
      AssignedCourierId: courierInfo?.courierId ?? null,
      AssignedCourierName: courierInfo?.courierName ?? null,
      AssignedCourierFirebaseUid: courierInfo?.courierUid ?? null,
    };

    if (orderData) {
      body.OrderDataJson = JSON.stringify(orderData);
    }

    await trendyolApi.post('/api/trendyol/order/update-status', body);
    console.log('[TrendyolSQL] Siparis durumu SQL\'de guncellendi:', packageId, newStatus);
  } catch (err) {
    console.error('[TrendyolSQL] update-status exception:', err);
  }
}

// SignalR `TrendyolOrderUpdated` event payload'ı backend `TrendyolYemekOrder` entity'sini
// olduğu gibi yolluyor; bu aynı raw format `mapSqlResponseToTrendyolOrder` ile parse edilir.
// useSignalR.ts handler'ı bu fonksiyonu import eder.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapSqlResponseToTrendyolOrder(raw: any): TrendyolOrder | null {
  try {
    const orderJson = raw.OrderDetailsJson ?? raw.orderDetailsJson;
    const isPaid = raw.IsPaid ?? raw.isPaid ?? false;
    const invoiceId = raw.InvoiceId ?? raw.invoiceId;
    const sqlStatus = raw.OrderStatus ?? raw.orderStatus;

    if (orderJson) {
      const parsed: TrendyolOrder = typeof orderJson === 'string' ? JSON.parse(orderJson) : orderJson;
      if (sqlStatus) {
        parsed.packageStatus = sqlStatus;
      }
      parsed.gtsPaid = !!isPaid && !!invoiceId;
      return parsed;
    }

    // Fallback: OrderDetailsJson yoksa flat alanlardan minimal TrendyolOrder olustur
    const id = String(raw.PackageId ?? raw.packageId ?? raw.Id ?? raw.id ?? '');
    if (!id) return null;

    const customerName = String(raw.CustomerName ?? raw.customerName ?? '');
    return {
      id,
      supplierId: raw.SupplierId ?? raw.supplierId ?? 0,
      storeId: raw.StoreId ?? raw.storeId ?? 0,
      orderCode: raw.OrderCode ?? raw.orderCode ?? '',
      storePickupSelected: false,
      deliveryType: raw.DeliveryType ?? raw.deliveryType ?? '',
      packageCreationDate: raw.OrderDate ? new Date(raw.OrderDate ?? raw.orderDate).getTime() : Date.now(),
      packageModificationDate: raw.LastModifiedDate ? new Date(raw.LastModifiedDate ?? raw.lastModifiedDate).getTime() : Date.now(),
      preparationTime: raw.PreparationTime ?? raw.preparationTime ?? 0,
      orderId: raw.OrderId ?? raw.orderId ?? '',
      orderNumber: raw.OrderNumber ?? raw.orderNumber ?? '',
      totalPrice: raw.TotalAmount ?? raw.totalAmount ?? 0,
      callCenterPhone: raw.CallCenterPhone ?? raw.callCenterPhone ?? '',
      customer: {
        id: 0,
        firstName: customerName.split(' ')[0] || '',
        lastName: customerName.split(' ').slice(1).join(' ') || '',
      },
      payment: { paymentType: raw.PaymentMethod ?? raw.paymentMethod ?? '', mealCard: null, onDelivery: null },
      address: {
        firstName: '', lastName: '', company: '',
        address1: raw.DeliveryAddress ?? raw.deliveryAddress ?? '',
        address2: '', city: '', cityCode: 0, cityId: 0,
        district: '', districtId: 0, neighborhoodId: 0, neighborhood: '',
        apartmentNumber: '', floor: '', doorNumber: '', addressDescription: '',
        postalCode: '', countryCode: '', latitude: '', longitude: '',
        phone: raw.CustomerPhone ?? raw.customerPhone ?? '',
      },
      packageStatus: sqlStatus || 'Created',
      lines: [],
      customerNote: raw.OrderNote ?? raw.orderNote ?? '',
      lastModifiedDate: raw.LastModifiedDate ? new Date(raw.LastModifiedDate ?? raw.lastModifiedDate).getTime() : Date.now(),
      isCourierNearby: false,
      cancelInfo: null,
      eta: raw.Eta ?? raw.eta ?? '',
      testPackage: raw.IsTestPackage ?? raw.isTestPackage ?? false,
      pickupEtaState: '',
      estimatedPickupTimeMin: 0,
      estimatedPickupTimeMax: 0,
      coupon: null,
      promotions: (() => {
        const disc = raw.DiscountAmount ?? raw.discountAmount;
        return disc && disc > 0 ? [{ promotionId: 0, description: 'Kampanya İndirimi', totalSellerAmount: disc }] : null;
      })(),
      userInformation: { appName: '' },
      gtsPaid: !!isPaid && !!invoiceId,
    };
  } catch (err) {
    console.error('[TrendyolSQL] mapSqlResponseToTrendyolOrder hatası:', err);
    return null;
  }
}

export async function getTodayOrdersFromSql(branchId?: string | null, page: number = 1, pageSize: number = 50, companyId?: string | null): Promise<TrendyolOrder[]> {
  try {
    const params: Record<string, string> = {
      page: String(page),
      pageSize: String(pageSize),
    };
    if (branchId) {
      params.branchId = branchId;
    }
    if (companyId) {
      params.companyId = companyId;
    }
    const response = await trendyolApi.get('/api/trendyol/order/today', { params });
    const result = response.data;
    console.log(`[TrendyolSQL] API yanıt:`, { Success: result.Success, DataCount: result.Data?.length, TotalCount: result.TotalCount, keys: Object.keys(result) });
    const data = result.Data ?? result.data;
    const success = result.Success ?? result.success;
    if (success && data && Array.isArray(data)) {
      const mapped = (data as Record<string, unknown>[])
        .map(mapSqlResponseToTrendyolOrder)
        .filter((o): o is TrendyolOrder => o !== null);
      console.log(`[TrendyolSQL] SQL'den ${mapped.length} sipariş yüklendi (toplam: ${result.TotalCount ?? result.totalCount})`, mapped.length > 0 ? { ilkSiparis: { id: mapped[0].id, storeId: mapped[0].storeId, storeIdType: typeof mapped[0].storeId, status: mapped[0].packageStatus } } : {});
      return mapped;
    }
    console.warn('[TrendyolSQL] SQL verisi boş veya Success false:', { success, dataLength: data?.length });
    return [];
  } catch (err) {
    console.error('[TrendyolSQL] today exception:', err);
    return [];
  }
}

/**
 * Belirli tarih aralığındaki Trendyol siparişlerini SQL'den getirir.
 * Geçmiş gün (örn. "son 7 gün") filtrelemesi için kullanılır.
 *
 * Backend: GET /api/trendyol/order/list?startDate&endDate&branchId&companyId&page&pageSize
 * (today endpoint'inin tarih bazlı varyantı, aynı response formatı)
 *
 * NOT: Canlı akışa dokunmaz — yalnızca PackageOrderScreen'deki "Tarih Filtresi"
 * tarafından çağrılır. Bugünün canlı state'i ayrı tutulmaya devam eder.
 */
export async function getOrdersFromSqlByDateRange(
  startDate: Date,
  endDate: Date,
  branchId?: string | null,
  page: number = 1,
  pageSize: number = 500,
  companyId?: string | null,
): Promise<TrendyolOrder[]> {
  try {
    // Backend repository `.Date` kullandığı için Z'siz local-format gönderiyoruz
    // (bkz. orderHistoryService.formatLocalDateTimeForApi). Aksi halde Türkiye
    // saati 11 May 00:00 → UTC 10 May 21:00 → `.Date` = 10 May → off-by-one.
    const fmt = (d: Date): string => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      return `${y}-${m}-${day}T${hh}:${mm}:${ss}`;
    };
    const params: Record<string, string> = {
      page: String(page),
      pageSize: String(pageSize),
      startDate: fmt(startDate),
      endDate: fmt(endDate),
    };
    if (branchId) params.branchId = branchId;
    if (companyId) params.companyId = companyId;

    const response = await trendyolApi.get('/api/trendyol/order/list', { params });
    const result = response.data;
    const data = result.Data ?? result.data;
    const success = result.Success ?? result.success;
    if (success && data && Array.isArray(data)) {
      const mapped = (data as Record<string, unknown>[])
        .map(mapSqlResponseToTrendyolOrder)
        .filter((o): o is TrendyolOrder => o !== null);
      console.log(`[TrendyolSQL][DateRange] ${mapped.length} sipariş yüklendi (${fmt(startDate)} - ${fmt(endDate)})`);
      return mapped;
    }
    console.warn('[TrendyolSQL][DateRange] Veri boş veya Success false:', { success, dataLength: data?.length });
    return [];
  } catch (err) {
    console.error('[TrendyolSQL][DateRange] exception:', err);
    return [];
  }
}

export function getCancelReasons(deliveryType: string): TrendyolCancelReason[] {
  if (deliveryType === 'STORE') {
    return TRENDYOL_CANCEL_REASONS;
  }
  return TRENDYOL_CANCEL_REASONS.filter(r => !r.model1Only);
}

// ============================================================================
// ÜRÜN KATALOĞU (Açıklama / description bilgisi için)
// ============================================================================

const PRODUCT_CACHE_TTL_MS = 60 * 60 * 1000; // 1 saat

export async function fetchProductDescriptions(
  credentials: TrendyolCredentials,
  localServiceUrl: string = 'http://localhost:3003',
): Promise<Map<number, string>> {
  const cacheKey = `trendyol_product_descriptions_${credentials.supplierId}_${credentials.storeId}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { ts, data } = JSON.parse(cached);
      if (Date.now() - ts < PRODUCT_CACHE_TTL_MS) {
        console.log(`[TrendyolProducts] Cache'den ${Object.keys(data).length} ürün açıklaması yüklendi`);
        return new Map(Object.entries(data).map(([k, v]) => [Number(k), String(v)]));
      }
    }
  } catch { /* ignore corrupt cache */ }

  const baseUrl = getBaseUrl();
  // Doğru endpoint (getRestaurantProducts):
  //   /integrator/product/meal/suppliers/{supplierId}/stores/{storeId}/products
  // Eski /mealgw/.../restaurants/... endpoint'i artık 5xx (556) dönüyor.
  const trendyolUrl = `${baseUrl}/integrator/product/meal/suppliers/${credentials.supplierId}/stores/${credentials.storeId}/products`;

  try {
    const response = await proxyFetch(localServiceUrl, trendyolUrl, 'GET', getAuthHeaders(credentials));
    if (!response.ok) {
      console.warn(`[TrendyolProducts] API ${response.status} döndü, açıklamalar atlanıyor (url=${trendyolUrl})`);
      return new Map();
    }

    const rawText = await response.text();
    console.log('[TrendyolProducts] API ham yanıt (ilk 500 karakter):', rawText.substring(0, 500));
    
    let json: any;
    try {
      json = JSON.parse(rawText);
    } catch (parseErr) {
      console.warn('[TrendyolProducts] JSON parse hatası:', parseErr);
      return new Map();
    }

    const products: any[] = json.products || json.data || (Array.isArray(json) ? json : []);
    console.log(`[TrendyolProducts] Parse sonucu: ${products.length} ürün bulundu, JSON top-level keys: [${Object.keys(json).join(', ')}]`);
    
    if (products.length > 0) {
      const sample = products[0];
      console.log('[TrendyolProducts] İlk ürün örneği:', JSON.stringify(sample).substring(0, 300));
    }

    const map = new Map<number, string>();
    let withDesc = 0;
    let withoutDesc = 0;

    for (const p of products) {
      const id = p.id || p.productId;
      const desc = (p.description || '').trim();
      if (id && desc) {
        map.set(Number(id), desc);
        withDesc++;
      } else if (id) {
        withoutDesc++;
      }
    }
    
    console.log(`[TrendyolProducts] Açıklama dağılımı: ${withDesc} açıklamalı, ${withoutDesc} açıklamasız`);

    // localStorage'a kaydet
    try {
      const obj: Record<string, string> = {};
      map.forEach((v, k) => { obj[String(k)] = v; });
      localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: obj }));
    } catch { /* quota exceeded etc. */ }

    console.log(`[TrendyolProducts] API'den ${map.size} ürün açıklaması yüklendi`);
    return map;
  } catch (err) {
    console.warn('[TrendyolProducts] Ürün kataloğu çekilemedi (mevcut akış etkilenmez):', err);
    return new Map();
  }
}

export function extractAllPackageItemIds(order: TrendyolOrder): string[] {
  const ids: string[] = [];
  for (const line of order.lines) {
    for (const item of line.items) {
      if (item.packageItemId && !item.isCancelled) {
        ids.push(item.packageItemId);
      }
    }
  }
  return ids;
}
